import { Component, ElementRef, Input, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DateFormatterService } from 'src/app/services/date-formatter.service';
import { ImageUploadService } from 'src/app/services/upload-service.service';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { MapComponent } from './map/map.component';


interface EventPrice {
  price?: number;
  description?: string;
}

interface EventBookModel {
  type?: string; // 'call' or 'navigationLink'
  value?: string | null;
}

interface EventDate {
  startDate?: Date | null;
  endDate?: Date | null;
  isRepetitive?: boolean;
}

interface EventTime {
  startTime?: string | null; // "HH:mm"
  endTime?: string | null;   // "HH:mm"
}

interface FeedFormData {
  active?: boolean  
  businessId?: string,
  businessBannerUrl?: string,
  businessName?: string,
  businessCountryISO?: string,
  type?: "post" | "activity" | "event",
  isFeatured?: boolean,
  skillIds?: string[],
  relatedVoucherIDs?: string[],
  description?: string;
  name?: string;
  title?: string;
  cost?: number;
  currency?: string;
  ageSuitability?: string;
  duration?: string;
  outdoorEvent?: boolean;
  date?: Date;
  locations ?: any;
  prices?: EventPrice[];
  eventWebUrl?: string;
  phoneNumber?: string;
  bookAction?: EventBookModel;
  eventDate?: EventDate;
  eventTime?: EventTime;
  [key: string]: any;
}

// Custom Validators
function dateNotInPastValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inputDate = new Date(control.value);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate < today ? { dateInPast: true } : null;
  };
}

function urlValidator(): ValidatorFn {
  // Accepts URLs with or without protocol
  const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i;
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  };
}

function phoneValidator(): ValidatorFn {
  // Accepts international and local phone numbers
  const phonePattern = /^\+?([0-9\s\-().]){7,}$/;
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    return phonePattern.test(control.value) ? null : { invalidPhone: true };
  };
}

function bookActionValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const type = group.get('type')?.value;
    const value = group.get('value')?.value;
    if (!value) return null;
    if (type === 'call') {
      // Phone validation
      const phonePattern = /^\+?([0-9\s\-().]){7,}$/;
      return phonePattern.test(value) ? null : { invalidBookPhone: true };
    } else if (type === 'navigationLink') {
      // URL validation
      const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i;
      return urlPattern.test(value) ? null : { invalidBookUrl: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss']
})
export class FeedComponent implements OnInit {

  @ViewChild('backdrop') backdrop!: ElementRef;
  @ViewChild('postToast') postToast!: ElementRef;
  @ViewChild('mapComponent') mapComponent!: MapComponent;
  @ViewChild('formExpand', { read: ElementRef }) formExpand!: ElementRef;
  @Input() businessData: any;

  isActiveTab: string = 'event';
  eventDateFilter: 'upcoming' | 'past' = 'upcoming';
  postForm: FormGroup;
  activityForm: FormGroup;
  eventForm: FormGroup;
  skillsList: any[] = [];
  vouchersList: any[] = [];
  selectedSkills: string[] = [];
  selectedVouchers: string[] = [];
  isBusinessEventCheckbox: boolean = false;
  isFeaturedCheckbox: boolean = false;
  isActiveCheckbox: boolean = true;
  resetMapTrigger = false;
  mapInitialLocation: any = null;
  // media variables
  selectedMedia: { name: string, size: number, invalid: boolean, errorMessage?: string, thumbnail?: any, isVideo?: boolean, existingMediaItem?: any }[] = [];
  validFiles: File[] = [];
  mediaValid: boolean = false;
  mediaUploaded: boolean = false;
  mediaReady: any = [];
  // event listing variables
  eventsList: any[] = [];
  selectedCountryISO: string = '';
  editMode: boolean = false;
  selectedEvent: any = null;
  pageSize: number = 20;
  eventMediaIndices: { [eventId: string]: number } = {}; // Track current media index for each event
  toastMessage: string = 'created';
  // pagination state for upcoming events
  private lastUpcomingEventDoc: any = null;
  isLoadingUpcoming: boolean = false;
  hasMoreUpcomingEvents: boolean = true;
  // pagination state for past events
  private lastPastEventDoc: any = null;
  isLoadingPast: boolean = false;
  hasMorePastEvents: boolean = true;

  constructor(
    private fb: FormBuilder,
    private firestore: AngularFirestore,
    private formatDate: DateFormatterService,
    private sanitizer: DomSanitizer,
    private imageUploadService: ImageUploadService,
    public loadingSpinnerService: LoadingSpinnerService,
    private renderer: Renderer2,
  ) {
    // Initialize forms for each tab
    this.postForm = this.fb.group({
      description: ['', [Validators.required]],
      date: ['', [Validators.required]]
    });
    this.activityForm = this.fb.group({
      name: ['', [Validators.required]],
      cost: [0, [Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      date: ['', [Validators.required]],
      currency: ['MKD'],
      description: ['', [Validators.required]],
      ageSuitability: ['', [Validators.required]],
      duration: [''],
      outdoorEvent: [false]
    });
    this.eventForm = this.fb.group({
      title: ['', [Validators.required]],
      prices: this.fb.array([
        this.fb.group({
          price: [0, [Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)]],
          description: ['']
        })
      ]),
      isFeatured: [false],
      currency: ['MKD'],
      description: ['', [Validators.required]],
      eventDate: this.fb.group({
        startDate: [null, [Validators.required, dateNotInPastValidator()]],
      }),
      eventTime: this.fb.group({
        startTime: ['', Validators.required],
      }),
      locations: ['', Validators.required],
      eventWebUrl: ['', urlValidator()],
      phoneNumber: ['', phoneValidator()],
      bookAction: this.fb.group({
        type: [''],
        value: ['']
      }, { validators: bookActionValidator() }),
      ageSuitability: ['', [Validators.required]],
      outdoorEvent: [false]
    });

    this.getAllSkillsAndVouchers();
    
    // Initialize selected country from business data
    if (this.businessData?.location?.countryISO) {
      this.selectedCountryISO = this.businessData.location.countryISO;
    } else if (this.businessData?.locations?.[0]?.countryISO) {
      this.selectedCountryISO = this.businessData.locations[0].countryISO;
    } else {
      this.selectedCountryISO = 'MKD'; // Default fallback
    }
  }

  get prices(): FormArray {
    return this.eventForm.get('prices') as FormArray;
  }

  addPrice() {
    (this.eventForm.get('prices') as any).push(
      this.fb.group({
        price: [0, [Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)]],
        description: ['']
      })
    );
  }

  removePrice(index: number) {
    (this.eventForm.get('prices') as any).removeAt(index);
  }

  toggleBackdrop(show: boolean): void {
    if (show) {
      this.backdrop.nativeElement.style.display = 'block';
    } else {
      this.selectedMedia = [];
      this.backdrop.nativeElement.style.display = 'none';
      if (!show && this.editMode) {
        this.cancelEdit();
      }
    }
  }

  setActiveTab(isActive: string): void {
    this.isActiveTab = isActive;
    if (isActive === 'event') {
      if (this.eventDateFilter === 'upcoming') {
        this.loadUpcomingEvents(true);
      } else {
        this.loadPastEvents(true);
      }
    }
  }

  onCheckboxChange(event: Event, type: 'skills' | 'vouchers'): void {
    const inputElement = event.target as HTMLInputElement;
    const id = inputElement.id;
    const isChecked = inputElement.checked;

    if (type === 'skills') {
      if (isChecked) {
        this.selectedSkills.push(id);
      } else {
        this.selectedSkills = this.selectedSkills.filter((skillId) => skillId !== id);
      }
      this.postForm.patchValue({ selectedSkills: this.selectedSkills });
    } else if (type === 'vouchers') {
      if (isChecked) {
        this.selectedVouchers.push(id);
      } else {
        this.selectedVouchers = this.selectedVouchers.filter((voucherId) => voucherId !== id);
      }
      this.postForm.patchValue({ selectedVouchers: this.selectedVouchers });
    }
  }

  getAllSkillsAndVouchers() {
    this.firestore.collection('Skills').get().subscribe({
      next: (skillsSnapshot) => {
        this.skillsList = skillsSnapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.addItemsToForms('skill', this.skillsList);
      },
      error: (error) => console.error('Error fetching skills:', error)
    });

    this.firestore.collection('GiftCards').get().subscribe({
      next: (vouchersSnapshot) => {
        this.vouchersList = vouchersSnapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          expirationDate: this.formatDate.formatDate(doc.data().expirationDate),
        }));
        this.vouchersList = this.vouchersList.filter((voucher) => voucher.businessId === this.businessData.id);
        this.addItemsToForms('voucher', this.vouchersList);
      },
      error: (error) => console.error('Error fetching vouchers:', error)
    });
  }

  // Dynamically add skills or vouchers to all forms
  addItemsToForms(itemType: 'skill' | 'voucher', items: any[]) {
    items.forEach((item) => {
      const controlName = `${itemType}_${item.id}`;

      this.postForm.addControl(controlName, this.fb.control(false));
      this.activityForm.addControl(controlName, this.fb.control(false));
      this.eventForm.addControl(controlName, this.fb.control(false));
    });
  }

  async onSubmit() {

    try {
      this.loadingSpinnerService.show();
      
      // Only attempt upload if there are files to upload
      if (this.validFiles.length > 0) {
        try {
          await this.handleFileUpload();
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          this.loadingSpinnerService.hide();
          alert('Failed to upload media files. Please try again. If the problem persists, check your connection and try again.');
          return; // Exit early - don't create the event
        }
      }

      let activeForm: FormGroup;
      switch (this.isActiveTab) {
        case 'post':
          activeForm = this.postForm;
          break;
        case 'activity':
          activeForm = this.activityForm;
          break;
        case 'event':
          activeForm = this.eventForm;
          break;
        default:
          throw new Error('Invalid tab type: ' + this.isActiveTab);
      }

      if (!activeForm.valid) {
        throw new Error('Form is invalid');
      }

      const formData: FeedFormData = activeForm.value;

      // Extract selected skills
      const selectedSkills = Object.keys(formData)
        .filter((key) => key.startsWith('skill_') && formData[key] === true)
        .map((key) => key.replace('skill_', ''));

      // Extract selected vouchers
      const selectedVouchers = Object.keys(formData)
        .filter((key) => key.startsWith('voucher_') && formData[key] === true)
        .map((key) => key.replace('voucher_', ''));

      // Remove skill_* and voucher_* keys from formData
      const filteredFormData = Object.keys(formData)
        .filter((key) => !key.startsWith('skill_') && !key.startsWith('voucher_'))
        .reduce((obj: { [key: string]: any }, key) => {
          obj[key] = formData[key];
          return obj;
        }, {});

      // Normalize eventWebUrl to ensure https:// prefix if missing
      if (this.isActiveTab === 'event' && typeof filteredFormData['eventWebUrl'] === 'string') {
        const rawUrl: string = (filteredFormData['eventWebUrl'] || '').trim();
        if (rawUrl) {
          // If protocol missing, prefix with https://
          if (!/^https?:\/\//i.test(rawUrl)) {
            filteredFormData['eventWebUrl'] = `https://${rawUrl}`;
          }
        }
      }

      // Normalize bookAction.value when type is navigationLink to ensure https:// prefix
      if (this.isActiveTab === 'event' && filteredFormData['bookAction'] && typeof filteredFormData['bookAction'] === 'object') {
        const action = filteredFormData['bookAction'] as EventBookModel;
        const isNav = action?.type === 'navigationLink';
        const rawValue = (action?.value as string | null) ?? '';
        if (isNav && typeof rawValue === 'string') {
          const trimmed = rawValue.trim();
          if (trimmed && !/^https?:\/\//i.test(trimmed)) {
            (filteredFormData['bookAction'] as any).value = `https://${trimmed}`;
          }
        }
      }

      // Handle media items - merge existing with new when editing
      let mediaItems = this.mediaReady;
      if (this.isActiveTab === 'event' && this.editMode && this.selectedEvent) {
        // When editing, preserve existing media that wasn't removed
        // Existing media items are those in selectedMedia that have existingMediaItem property
        const preservedExistingMedia = this.selectedMedia
          .filter(m => !m.invalid && m.existingMediaItem)
          .map(m => m.existingMediaItem);
        
        // Merge preserved existing media with new media
        mediaItems = [...preservedExistingMedia, ...this.mediaReady];
      }

      let finalPayload: FeedFormData = {
        active: this.isActiveCheckbox,
        businessId: this.businessData.id,
        businessBannerUrl: this.businessData.businessBannerUrl,
        businessName: this.businessData.name,
        businessCountryISO: this.businessData.location.countryISO,
        type: this.isActiveTab,
        ...filteredFormData,
        skillIDs: selectedSkills,
        relatedVoucherIDs: selectedVouchers,
        mediaItems: mediaItems,
        // date: new Date(activeForm.get('date')?.value),
      };

      if (this.isActiveTab === 'event') {
        // Ensure eventDate.startDate is stored as a Date object in Firestore,
        // combining the selected date (eventDate.startDate) and time (eventTime.startTime)
        const rawNestedStartDate = this.eventForm.get('eventDate.startDate')?.value; // e.g. "2025-12-01"
        const rawStartTime = this.eventForm.get('eventTime.startTime')?.value;       // e.g. "18:00"

        let normalizedNestedStartDate: Date | null = null;

        if (rawNestedStartDate) {
          const date = new Date(rawNestedStartDate);

          if (rawStartTime) {
            const [hoursStr, minutesStr] = String(rawStartTime).split(':');
            const hours = Number(hoursStr);
            const minutes = Number(minutesStr);
            date.setHours(hours, minutes, 0, 0);
          }

          normalizedNestedStartDate = date;
        }

        (finalPayload as any).eventDate = {
          ...(finalPayload as any).eventDate,
          startDate: normalizedNestedStartDate,
        } as EventDate;

        // Remove bookAction if it's empty (no type or no value)
        if (finalPayload.bookAction) {
          const bookAction = finalPayload.bookAction as EventBookModel;
          const hasType = bookAction.type && String(bookAction.type).trim() !== '';
          const hasValue = bookAction.value && String(bookAction.value).trim() !== '';
          if (!hasType || !hasValue) {
            delete finalPayload.bookAction;
          }
        }

        if (!this.isBusinessEventCheckbox) {
          // If not checked exclude business-related data
          delete finalPayload.businessId;
          delete finalPayload.businessBannerUrl;
          delete finalPayload.businessName;
          delete finalPayload.businessCountryISO;

          const locationValue = this.eventForm.get('locations')?.value;
          if (locationValue[0].countryISO) {
            finalPayload.businessCountryISO = locationValue[0].countryISO;
          }
        }
        delete finalPayload.relatedVoucherIDs;
        finalPayload.isFeatured = this.isFeaturedCheckbox;
      } else {
        finalPayload.locations = this.businessData.location;
      }
      // Create or update document
      try {
        if (this.isActiveTab === 'event' && this.editMode && this.selectedEvent) {
          // Update existing event
          await this.firestore.collection('Events').doc(this.selectedEvent.id).update(finalPayload);
          this.toastMessage = 'updated';
          this.postToast.nativeElement.classList.add('show');
          setTimeout(() => {
            this.postToast.nativeElement.classList.remove('show');
          }, 2000);
          this.cancelEdit();
          if (this.eventDateFilter === 'upcoming') {
            this.loadUpcomingEvents(true);
          } else {
            this.loadPastEvents(true);
          }
          this.closeSidebar();
        } else {
          // Create new document
          const docId = this.firestore.createId();
          this.isActiveTab === 'event' ? await this.firestore.collection('Events').doc(docId).set({ ...finalPayload, id: docId }) : await this.firestore.collection('Feed').doc(docId).set({ ...finalPayload, id: docId });
          this.toastMessage = 'created';
          this.postToast.nativeElement.classList.add('show');
          setTimeout(() => {
            this.postToast.nativeElement.classList.remove('show');
          }, 2000);
          
          if (this.isActiveTab === 'event') {
            if (this.eventDateFilter === 'upcoming') {
              this.loadUpcomingEvents(true);
            } else {
              this.loadPastEvents(true);
            }
          }
          this.closeSidebar();
        }
        
        // Reset form to initial values defined in constructor
        if (this.isActiveTab === 'post') {
          this.postForm.reset();
        } else if (this.isActiveTab === 'activity') {
          this.activityForm.reset({
            cost: 0,
            currency: 'MKD',
            duration: '',
            outdoorEvent: false
          });
        } else if (this.isActiveTab === 'event') {
          this.eventForm.reset({
            cost: 0,
            currency: 'MKD',
            outdoorEvent: false,
            isFeatured : false
          });
          this.resetMap();
          this.cancelEdit();
        }
        this.loadingSpinnerService.hide();
      } catch (error) {
        console.error('Error saving document: ', error);
        this.loadingSpinnerService.hide();
      }
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      this.resetForm();
      this.loadingSpinnerService.hide();
    }
  }
  
  resetMap() {
    this.resetMapTrigger = true;
    this.mapInitialLocation = null;
    // Reset the trigger after a short delay so it can be triggered again
    setTimeout(() => {
      this.resetMapTrigger = false;
    });
  }

  private resetForm(): void {
    this.selectedMedia.forEach(media => {
      if (media.thumbnail) {
        URL.revokeObjectURL(media.thumbnail as string);
      }
    });
    this.selectedMedia = [];
    this.validFiles = [];
    this.mediaUploaded = false;
    this.selectedSkills = [];
    this.selectedVouchers = [];
    this.mediaReady = [];
    this.mediaValid = false;
  }

  ngOnDestroy(): void {
    // Cleanup object URLs
    this.resetForm();
  }

  isBusinessEvent(event: Event): void {
    this.isBusinessEventCheckbox = (event.target as HTMLInputElement).checked;

    const locationControl = this.eventForm.get('locations');
    if (this.isBusinessEventCheckbox) {
      locationControl?.clearValidators();
    } else {
      locationControl?.setValidators([Validators.required]);
    }
    locationControl?.updateValueAndValidity();
  }

  isFeaturedChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.isFeaturedCheckbox = checked;
    this.eventForm.patchValue({ isFeatured: checked });
  }

  isActiveChange(event: Event): void {
    this.isActiveCheckbox = (event.target as HTMLInputElement).checked;
  }

  onPlaceSelected(place: any): void {
    this.eventForm.patchValue({ locations: place });
  }
  
  // media upload (images and videos)
  onMediaChange(event: any) {
    const files = event.target.files;

    if ((files.length + this.selectedMedia.length) > 10) {
      alert("Maximum number of files is 10!");
      return;
    }

    this.mediaValid = true;

    for (let file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type === 'video/mp4';
      let invalid = false;
      let errorMessage = '';
      let thumbnail: SafeUrl | string = '';

      if (isImage || isVideo) {
        if (isImage && !['image/png', 'image/jpeg'].includes(file.type)) {
          invalid = true;
          errorMessage = 'Invalid image type. Only PNG and JPEG are allowed.';
        } else if (isVideo && file.size > 10 * 1024 * 1024) {
          invalid = true;
          errorMessage = 'Video size exceeds 10 MB.';
        }

        // For valid media, process the thumbnail (image or video)
        if (!invalid) {
          thumbnail = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
          this.validFiles.push(file);
          this.mediaUploaded = true;
        }

        this.selectedMedia.push({ name: file.name, size: file.size, invalid, errorMessage, thumbnail, isVideo });
      } else {
        invalid = true;
        errorMessage = 'Invalid file type. Only PNG/JPEG images and MP4 videos are allowed.';
      }

      if (invalid) {
        this.mediaValid = false;
      }
    }

    event.target.value = null;
  }

  removeFile(index: number) {
    const removedFile = this.selectedMedia.splice(index, 1)[0];

    if (!removedFile.invalid) {
      const fileIndex = this.validFiles.findIndex(file => file.name === removedFile.name);
      if (fileIndex !== -1) {
        this.validFiles.splice(fileIndex, 1);
      }
    }

    // Check media validity after removal
    this.mediaValid = this.selectedMedia.every(file => !file.invalid);

    if (this.selectedMedia.some(file => file.thumbnail)) {
      this.mediaUploaded = true;
    } else {
      this.mediaUploaded = false;
    }
  }

  async handleFileUpload() {
    const countryISO = this.isBusinessEventCheckbox ? this.businessData.location.countryISO : this.eventForm.get('locations')?.value[0].countryISO;
    this.mediaReady = await this.imageUploadService.compressAndUploadFiles(this.isActiveTab === 'event' ? 'Events' : 'Activities', this.businessData.id, this.validFiles, countryISO);
    this.mediaReady = this.mediaReady.map((item: any) => {
      const { link, ...rest } = item;
      return {...rest,
        [this.isActiveTab === 'event' ? 'mediaUrl' : 'mediaName']: link
      };
    });
  }

  ngOnInit(): void {
    if (this.isActiveTab === 'event') {
      this.loadUpcomingEvents(true);
    }
  }

  private mapEventsSnapshot(snapshot: any): any[] {
    return snapshot.docs.map((doc: any) => {
      const data = doc.data() as any;
      // Handle Firestore Timestamp
      let startDate: Date | null = null;
      const rawStartDate = data.eventDate?.startDate;

      if (rawStartDate) {
        if (rawStartDate.toDate && typeof rawStartDate.toDate === 'function') {
          // Firestore Timestamp
          startDate = rawStartDate.toDate();
        } else if (rawStartDate.seconds) {
          // Firestore Timestamp format { seconds, nanoseconds }
          startDate = new Date(rawStartDate.seconds * 1000);
        } else if (rawStartDate instanceof Date) {
          startDate = rawStartDate;
        } else if (typeof rawStartDate === 'string' || typeof rawStartDate === 'number') {
          startDate = new Date(rawStartDate);
        }

        // Validate the date
        if (startDate && isNaN(startDate.getTime())) {
          startDate = null;
        }
      }

      // Format date for display
      let formattedStartDate = '';
      if (startDate && !isNaN(startDate.getTime())) {
        const day = startDate.getDate().toString().padStart(2, '0');
        const month = (startDate.getMonth() + 1).toString().padStart(2, '0');
        const year = startDate.getFullYear();
        formattedStartDate = `${day}.${month}.${year}`;
      }

      return {
        id: doc.id,
        ...data,
        eventDate: {
          ...(data.eventDate || {}),
          startDate: startDate
        },
        formattedStartDate: formattedStartDate
      };
    });
  }

  loadUpcomingEvents(reset: boolean = false): void {
    if (this.isLoadingUpcoming) {
      return;
    }

    if (reset) {
      this.eventsList = [];
      this.lastUpcomingEventDoc = null;
      this.hasMoreUpcomingEvents = true;
    }

    if (!this.hasMoreUpcomingEvents) {
      return;
    }

    this.isLoadingUpcoming = true;

    const now = new Date();

    this.firestore.collection('Events', ref => {
      let query: any = ref
        .where('eventDate.startDate', '>=', now)
        .where('businessCountryISO', '==', this.selectedCountryISO)
        .orderBy('eventDate.startDate', 'asc')
        .limit(this.pageSize);

      if (this.lastUpcomingEventDoc) {
        query = query.startAfter(this.lastUpcomingEventDoc);
      }

      return query;
    }).get().subscribe({
      next: (snapshot) => {
        const newEvents = this.mapEventsSnapshot(snapshot);

        if (snapshot.docs.length > 0) {
          this.lastUpcomingEventDoc = snapshot.docs[snapshot.docs.length - 1];
        }

        if (snapshot.docs.length < this.pageSize) {
          this.hasMoreUpcomingEvents = false;
        }

        this.eventsList = [...this.eventsList, ...newEvents];
        this.isLoadingUpcoming = false;
      },
      error: (error) => {
        console.error('Error fetching upcoming events:', error);
        this.isLoadingUpcoming = false;
      }
    });
  }

  loadPastEvents(reset: boolean = false): void {
    if (this.isLoadingPast) {
      return;
    }

    if (reset) {
      this.eventsList = [];
      this.lastPastEventDoc = null;
      this.hasMorePastEvents = true;
    }

    if (!this.hasMorePastEvents) {
      return;
    }

    this.isLoadingPast = true;

    const now = new Date();

    this.firestore.collection('Events', ref => {
      let query: any = ref
        .where('eventDate.startDate', '<', now)
        .where('businessCountryISO', '==', this.selectedCountryISO)
        .orderBy('eventDate.startDate', 'desc')
        .limit(this.pageSize);

      if (this.lastPastEventDoc) {
        query = query.startAfter(this.lastPastEventDoc);
      }

      return query;
    }).get().subscribe({
      next: (snapshot) => {
        const newEvents = this.mapEventsSnapshot(snapshot);

        if (snapshot.docs.length > 0) {
          this.lastPastEventDoc = snapshot.docs[snapshot.docs.length - 1];
        }

        if (snapshot.docs.length < this.pageSize) {
          this.hasMorePastEvents = false;
        }

        this.eventsList = [...this.eventsList, ...newEvents];
        this.isLoadingPast = false;
      },
      error: (error) => {
        console.error('Error fetching past events:', error);
        this.isLoadingPast = false;
      }
    });
  }

  onCountryChange(countryISO: string): void {
    this.selectedCountryISO = countryISO;
    if (this.eventDateFilter === 'upcoming') {
      this.loadUpcomingEvents(true);
    } else {
      this.loadPastEvents(true);
    }
  }

  setEventDateFilter(filter: 'upcoming' | 'past'): void {
    if (this.eventDateFilter === filter) {
      if (filter === 'past' && this.eventsList.length === 0 && this.hasMorePastEvents) {
        this.loadPastEvents(true);
      } else if (filter === 'upcoming' && this.eventsList.length === 0 && this.hasMoreUpcomingEvents) {
        this.loadUpcomingEvents(true);
      }
      return;
    }

    this.eventDateFilter = filter;

    if (filter === 'upcoming') {
      this.loadUpcomingEvents(true);
    } else {
      this.loadPastEvents(true);
    }
  }

  editEvent(event: any): void {
    this.editMode = true;
    this.selectedEvent = event;
    
    // Expand the form
    const formExpand = document.getElementById('formExpand');
    if (formExpand && !formExpand.classList.contains('show')) {
      const bsCollapse = new (window as any).bootstrap.Collapse(formExpand, {
        toggle: true
      });
    }

    // Populate form with event data
    this.populateEventForm(event);
  }

  populateEventForm(event: any): void {
    // Reset form first
    this.eventForm.reset();
    this.resetForm();
    
    // Set basic fields
    this.eventForm.patchValue({
      title: event.title || '',
      description: event.description || '',
      currency: event.currency || 'MKD',
      eventWebUrl: event.eventWebUrl || '',
      phoneNumber: event.phoneNumber || '',
      ageSuitability: event.ageSuitability || '',
      outdoorEvent: event.outdoorEvent ?? false,
      isFeatured: event.isFeatured ?? false
    });

    // Set business event checkbox
    this.isBusinessEventCheckbox = !!event.businessId;
    this.isFeaturedCheckbox = event.isFeatured ?? false;
    this.isActiveCheckbox = event.active ?? true;

    // Set event date
    if (event.eventDate?.startDate) {
      let startDate: Date;
      if (event.eventDate.startDate.toDate) {
        startDate = event.eventDate.startDate.toDate();
      } else if (event.eventDate.startDate instanceof Date) {
        startDate = event.eventDate.startDate;
      } else {
        startDate = new Date(event.eventDate.startDate);
      }
      const dateStr = startDate.toISOString().split('T')[0];
      this.eventForm.get('eventDate')?.patchValue({
        startDate: dateStr
      });
    }

    // Set event time
    if (event.eventTime?.startTime) {
      this.eventForm.get('eventTime')?.patchValue({
        startTime: event.eventTime.startTime
      });
    }

    // Set locations
    if (event.locations) {
      this.eventForm.patchValue({
        locations: event.locations
      });
      // Set initial location for map component
      this.mapInitialLocation = event.locations;
    }

    // Set prices
    if (event.prices && Array.isArray(event.prices)) {
      // Clear existing prices
      while (this.prices.length !== 0) {
        this.prices.removeAt(0);
      }
      // Add prices from event
      event.prices.forEach((price: EventPrice) => {
        this.prices.push(this.fb.group({
          price: [price.price || 0, [Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/)]],
          description: [price.description || '']
        }));
      });
    }

    // Set book action
    if (event.bookAction) {
      this.eventForm.get('bookAction')?.patchValue({
        type: event.bookAction.type || '',
        value: event.bookAction.value || ''
      });
    }

    // Set skills
    if (event.skillIDs && Array.isArray(event.skillIDs)) {
      event.skillIDs.forEach((skillId: string) => {
        const controlName = `skill_${skillId}`;
        if (this.eventForm.get(controlName)) {
          this.eventForm.patchValue({ [controlName]: true });
        }
      });
    }

    // Set media
    if (event.mediaItems && Array.isArray(event.mediaItems)) {
      event.mediaItems.forEach((mediaItem: any) => {
        const mediaUrl = mediaItem.mediaUrl || mediaItem.link;
        if (mediaUrl) {
          const isVideo = mediaItem.type === 'video' || mediaUrl.includes('.mp4');
          this.selectedMedia.push({
            name: mediaItem.name || 'Event Media',
            size: mediaItem.size || 0,
            invalid: false,
            thumbnail: this.sanitizer.bypassSecurityTrustUrl(mediaUrl),
            isVideo: isVideo,
            existingMediaItem: mediaItem // Store original media item for later reference
          });
          this.mediaUploaded = true;
          this.mediaValid = true;
        }
      });
    }
  }

  cancelEdit(): void {
    this.editMode = false;
    this.selectedEvent = null;
    this.resetForm();
    this.eventForm.reset();
    this.isBusinessEventCheckbox = false;
    this.isFeaturedCheckbox = false;
    this.isActiveCheckbox = true;
    this.mapInitialLocation = null;
    
    // Reset prices to default
    while (this.prices.length > 1) {
      this.prices.removeAt(1);
    }
    if (this.prices.length > 0) {
      this.prices.at(0)?.patchValue({ price: 0, description: '' });
    }
  }

  closeSidebar(): void {
    const collapseElement = this.formExpand?.nativeElement;
    if (collapseElement) {
      setTimeout(() => {
        this.renderer.addClass(collapseElement, 'collapsing');
        this.renderer.removeClass(collapseElement, 'collapse');
        this.renderer.removeClass(collapseElement, 'show');
        this.toggleBackdrop(false);
      }, 350);
    }
  }

  playVideoOnHover(event: MouseEvent): void {
    const videoContainer = (event.currentTarget as HTMLElement);
    const video = videoContainer.querySelector('video') as HTMLVideoElement;
    if (video) {
      video.play().catch(err => {
        // Handle autoplay restrictions
        console.log('Video autoplay prevented:', err);
      });
      // Hide play button overlay when playing
      const overlay = videoContainer.querySelector('.play-button-overlay') as HTMLElement;
      if (overlay) {
        overlay.style.opacity = '0';
      }
    }
  }

  pauseVideoOnHover(event: MouseEvent): void {
    const videoContainer = (event.currentTarget as HTMLElement);
    const video = videoContainer.querySelector('video') as HTMLVideoElement;
    if (video) {
      video.pause();
      video.currentTime = 0; // Reset to beginning
      // Show play button overlay when paused
      const overlay = videoContainer.querySelector('.play-button-overlay') as HTMLElement;
      if (overlay) {
        overlay.style.opacity = '1';
      }
    }
  }

  getCurrentMediaIndex(eventId: string): number {
    return this.eventMediaIndices[eventId] || 0;
  }

  getCurrentMediaItem(event: any): any {
    if (!event.mediaItems || event.mediaItems.length === 0) {
      return null;
    }
    const index = this.getCurrentMediaIndex(event.id);
    return event.mediaItems[index] || event.mediaItems[0];
  }

  navigateMedia(event: any, direction: 'prev' | 'next', $event: MouseEvent): void {
    $event.stopPropagation(); // Prevent card click
    if (!event.mediaItems || event.mediaItems.length <= 1) {
      return;
    }
    
    // Find the media container in the DOM
    const cardElement = ($event.currentTarget as HTMLElement).closest('.event-card');
    if (!cardElement) return;
    
    const mediaContainer = cardElement.querySelector('.event-media');
    if (!mediaContainer) return;
    
    const mediaItem = mediaContainer.querySelector('.media-item') as HTMLElement;
    if (!mediaItem) return;
    
    // Pause any playing video before transition
    const video = mediaContainer.querySelector('video') as HTMLVideoElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    
    // Fade out current media
    mediaItem.classList.add('media-fade-out');
    
    // Wait for fade-out, then change media
    setTimeout(() => {
      const currentIndex = this.getCurrentMediaIndex(event.id);
      let newIndex: number;
      
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % event.mediaItems.length;
      } else {
        newIndex = currentIndex === 0 ? event.mediaItems.length - 1 : currentIndex - 1;
      }
      
      this.eventMediaIndices[event.id] = newIndex;
      
      // Force Angular change detection
      this.eventsList = [...this.eventsList];
      
      // Fade in new media
      setTimeout(() => {
        const newMediaItem = mediaContainer.querySelector('.media-item') as HTMLElement;
        if (newMediaItem) {
          newMediaItem.classList.remove('media-fade-out');
        }
      }, 50);
    }, 200); // Half of transition duration (400ms / 2)
  }
}
